import React, { useState, useEffect, useRef } from 'react';
import { Calculator, Eye, EyeOff, Activity, Target, Box, Columns, Square, Settings, AlertTriangle } from 'lucide-react';
import type { Wall, Room, FloorPlanElement } from '../types';
import { calculateHLPLength, calculatePWLength, calculateSWLength, calculateRooms } from '../utils/floorPlanUtils';
import { memoizedCalculateRooms, PerformanceMonitor, clearAllCaches } from '../utils/performanceOptimizations';
import { repairElementRecognition, DEFAULT_ELEMENT_REPAIR_OPTIONS, type ElementRepairResult } from '../utils/elementRepairTool';

interface OnDemandCalculationsPanelProps {
  currentWalls: Wall[];
  roomCustomLabels: Record<string, string>;
  className?: string;
  elements?: FloorPlanElement[];
  currentFloor?: number;
  onElementsRepaired?: (repairedElements: FloorPlanElement[], report: string) => void;
  floorMetrics?: {
    hlpLength: number;
    pwLength: number;
    hlpSegments: number[];
    pwSegments: number[];
  };
}

interface CalculationData {
  hlpLength: number;
  pwLength: number;
  swLength: number;
  totalArea: number;
  lastCalculated: number;
  totalRooms: number;
}

interface PerformanceMetrics {
  calculateRooms?: { average: number; count: number; max: number };
  renderCanvasView?: { average: number; count: number; max: number };
  drawCanvas?: { average: number; count: number; max: number };
  cacheHitRate: number;
  averageCalculationTime: number;
  memoryUsage: number;
}

const OnDemandCalculationsPanel: React.FC<OnDemandCalculationsPanelProps> = ({
  currentWalls,
  roomCustomLabels,
  className = '',
  elements,
  currentFloor,
  onElementsRepaired,
  floorMetrics
}) => {
  const [calculations, setCalculations] = useState<CalculationData | null>(null);
  const [showPerformanceMetrics, setShowPerformanceMetrics] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    cacheHitRate: 0,
    averageCalculationTime: 0,
    memoryUsage: 0
  });
  
  // Element repair state
  const [showElementRepair, setShowElementRepair] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<ElementRepairResult | null>(null);
  const [lastRepairTimestamp, setLastRepairTimestamp] = useState<number | null>(null);
  
  // Auto-calculate on component mount and when walls change
  useEffect(() => {
    const calculateMetrics = async () => {
      // Use floorMetrics if available, otherwise recalculate
      if (floorMetrics) {
        console.log('📊 [OnDemandCalculationsPanel] Using floorMetrics:', floorMetrics);
      } else {
        console.log('📊 [OnDemandCalculationsPanel] Recalculating from walls');
      }
      
      const hlpLength = floorMetrics?.hlpLength ?? calculateHLPLength(currentWalls);
      const pwLength = floorMetrics?.pwLength ?? calculatePWLength(currentWalls);
      const swLength = calculateSWLength(currentWalls);
      
      // Use memoized room calculation with 'explicitRoomCreation' context to get all rooms for area calculation
      const rooms = memoizedCalculateRooms(currentWalls, roomCustomLabels, 'explicitRoomCreation', undefined, undefined);
      // Calculate total area directly from all rooms
      const totalArea = rooms.reduce((sum: number, room: Room) => sum + room.area, 0);
      
      const newCalculations = {
        hlpLength,
        pwLength,
        swLength,
        totalArea,
        lastCalculated: Date.now(),
        totalRooms: rooms.length
      };
      
      console.log('📊 [OnDemandCalculationsPanel] Setting calculations:', {
        hlpLength: newCalculations.hlpLength,
        pwLength: newCalculations.pwLength,
        totalArea: newCalculations.totalArea
      });
      
      setCalculations(newCalculations);
      
      // Update performance metrics
      updatePerformanceMetrics();
    };

    calculateMetrics();
  }, [currentWalls, roomCustomLabels, floorMetrics]);

  // Handle element repair
  const handleElementRepair = async () => {
    if (!elements || !onElementsRepaired) {
      console.warn('[ElementRepair] Cannot repair: missing elements or callback');
      return;
    }

    setIsRepairing(true);
    try {
      console.log('[ElementRepair] Starting element repair process...');
      
      const result = repairElementRecognition(
        elements,
        currentWalls,
        roomCustomLabels,
        currentFloor || 1,
        DEFAULT_ELEMENT_REPAIR_OPTIONS
      );
      
      setRepairResult(result);
      setLastRepairTimestamp(Date.now());
      
      if (result.elementsRepaired) {
        // Generate user-friendly report
        const report = generateRepairReport(result);
        
        // Call the callback to update elements in parent component
        onElementsRepaired(result.repairedElements, report);
        
        console.log('[ElementRepair] Repair completed successfully:', result.reportSummary);
      } else {
        console.log('[ElementRepair] No issues found - elements are already properly configured');
      }
      
    } catch (error) {
      console.error('[ElementRepair] Error during repair:', error);
      setRepairResult({
        elementsRepaired: false,
        issuesFound: ['Repair process failed due to an error'],
        issuesFixed: [],
        repairedElements: elements,
        reportSummary: {
          totalElements: elements.length,
          elementsWithIssues: 0,
          elementsFixed: 0,
          missingWallIdsFixed: 0,
          missingRoomAssignmentsFixed: 0,
          invalidPositionsFixed: 0,
          missingPropertiesFixed: 0,
          stableIdsRegenerated: 0,
          formatsUpdated: 0,
          lightingElementsFixed: 0,
          radiatorElementsFixed: 0,
          windowElementsFixed: 0,
        }
      });
    } finally {
      setIsRepairing(false);
    }
  };

  // Generate user-friendly repair report
  const generateRepairReport = (result: ElementRepairResult): string => {
    const { reportSummary } = result;
    
    let report = `Element Recognition Repair Summary:\n\n`;
    report += `📊 Overall Results:\n`;
    report += `• Total elements processed: ${reportSummary.totalElements}\n`;
    report += `• Elements with issues: ${reportSummary.elementsWithIssues}\n`;
    report += `• Elements successfully repaired: ${reportSummary.elementsFixed}\n\n`;
    
    if (reportSummary.elementsFixed > 0) {
      report += `🔧 Specific Repairs:\n`;
      if (reportSummary.lightingElementsFixed > 0) {
        report += `• Lighting elements fixed: ${reportSummary.lightingElementsFixed}\n`;
      }
      if (reportSummary.radiatorElementsFixed > 0) {
        report += `• Radiator elements fixed: ${reportSummary.radiatorElementsFixed}\n`;
      }
      if (reportSummary.windowElementsFixed > 0) {
        report += `• Window elements fixed: ${reportSummary.windowElementsFixed}\n`;
      }
      if (reportSummary.missingWallIdsFixed > 0) {
        report += `• Missing wall IDs restored: ${reportSummary.missingWallIdsFixed}\n`;
      }
      if (reportSummary.missingRoomAssignmentsFixed > 0) {
        report += `• Missing room assignments restored: ${reportSummary.missingRoomAssignmentsFixed}\n`;
      }
    } else {
      report += `✅ All elements are properly configured for the new recognition system!\n`;
    }
    
    return report;
  };

  // Update performance metrics
  const updatePerformanceMetrics = () => {
    const metrics = PerformanceMonitor.getAllMetrics();
    
    // Calculate additional metrics
    const roomCalculationMetrics = metrics.roomCalculation || metrics['room-calculation'] || null;
    const cacheHitRate = roomCalculationMetrics?.count 
      ? Math.min(100, (roomCalculationMetrics.count / (roomCalculationMetrics.count + 10)) * 100) // Simplified cache hit rate estimation
      : 0;
    
    const averageCalculationTime = roomCalculationMetrics?.average || 0;
    
    // Estimate memory usage (simplified calculation)
    const memoryUsage = (performance as any).memory 
      ? (performance as any).memory.usedJSHeapSize / (1024 * 1024) 
      : 0;
    
    setPerformanceMetrics({
      calculateRooms: roomCalculationMetrics,
      renderCanvasView: metrics.renderCanvasView || metrics['render-canvas'] || undefined,
      drawCanvas: metrics.drawCanvas || metrics['draw-canvas'] || undefined,
      cacheHitRate,
      averageCalculationTime,
      memoryUsage
    });
  };

  // Clear performance caches
  const handleClearCaches = () => {
    clearAllCaches();
    updatePerformanceMetrics();
  };

  const formatTime = (ms: number): string => {
    if (ms < 1) return `${(ms * 1000).toFixed(0)}μs`;
    if (ms < 1000) return `${ms.toFixed(1)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getPerformanceColor = (average: number): string => {
    if (average <= 16) return 'text-green-600'; // Good - under 60fps target
    if (average <= 33) return 'text-yellow-600'; // OK - under 30fps
    return 'text-red-600'; // Poor - over 30fps
  };

  const getPerformanceStatus = (average: number): string => {
    if (average <= 16) return '✅ Excellent';
    if (average <= 33) return '⚠️ Acceptable';
    return '❌ Needs Optimization';
  };

  if (!calculations) {
    return (
      <div className={`${className} bg-gradient-to-br from-slate-50 via-white to-slate-50 rounded-2xl shadow-lg border border-slate-200/50 p-6`}>
        <div className="flex items-center justify-center gap-3 text-slate-500">
          <div className="p-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <Calculator size={20} className="animate-spin text-white" />
          </div>
          <span className="text-base font-medium">Calculating measurements...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Modern Live Calculations Panel */}
      <div className="bg-gradient-to-br from-white via-slate-50 to-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
        {/* Compact Header with Gradient */}
        <div className="relative bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 px-3 py-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-white/20 rounded-lg backdrop-blur-sm">
                <Calculator size={14} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-white">Live Calculations</h3>
                <p className="text-slate-100 text-xs hidden sm:block">Real-time measurements</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Compact Control Buttons */}
              <div className="hidden lg:flex items-center gap-1">
                <button
                  onClick={() => setShowPerformanceMetrics(!showPerformanceMetrics)}
                  className={`px-2 py-1 rounded text-xs transition-all duration-200 ${
                    showPerformanceMetrics 
                      ? 'bg-white/25 text-white' 
                      : 'bg-white/10 text-slate-200 hover:bg-white/20'
                  }`}
                >
                  <Activity size={10} className="inline mr-0.5" />
                  Perf
                </button>
                {/* Element Repair Tool */}
                {elements && elements.length > 0 && (
                  <button
                    onClick={() => setShowElementRepair(!showElementRepair)}
                    className={`px-2 py-1 rounded text-xs transition-all duration-200 ${
                      showElementRepair 
                        ? 'bg-white/25 text-white' 
                        : 'bg-white/10 text-slate-200 hover:bg-white/20'
                    }`}
                    title="Fix element recognition issues for old files"
                  >
                    <Settings size={10} className="inline mr-0.5" />
                    Fix
                  </button>
                )}
              </div>
              {/* Compact Live Indicator */}
              <div className="flex items-center gap-1 px-2 py-1 bg-white/20 rounded-lg backdrop-blur-sm">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-white">LIVE</span>
              </div>
            </div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-700/0 via-white/5 to-slate-700/0 pointer-events-none"></div>
        </div>

        {/* Compact Performance Metrics */}
        {showPerformanceMetrics && performanceMetrics && (
          <div className="mx-2 my-2 p-2 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-gradient-to-r from-purple-500 to-indigo-600 rounded">
                  <Activity size={12} className="text-white" />
                </div>
                <h4 className="font-bold text-xs text-purple-800">Performance</h4>
              </div>
              <button
                onClick={handleClearCaches}
                className="px-2 py-1 bg-gradient-to-r from-purple-600 to-purple-500 text-white text-xs rounded hover:from-purple-700 hover:to-purple-600 transition-all duration-200"
              >
                Clear
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/60 rounded p-2">
                <p className="text-xs text-purple-600 font-medium mb-1">Cache</p>
                <p className="text-sm font-bold text-purple-700">{performanceMetrics.cacheHitRate.toFixed(1)}%</p>
              </div>
              <div className="bg-white/60 rounded p-2">
                <p className="text-xs text-purple-600 font-medium mb-1">Time</p>
                <p className="text-sm font-bold text-purple-700">{performanceMetrics.averageCalculationTime.toFixed(1)}ms</p>
              </div>
              <div className="bg-white/60 rounded p-2">
                <p className="text-xs text-purple-600 font-medium mb-1">Memory</p>
                <p className="text-sm font-bold text-purple-700">{performanceMetrics.memoryUsage.toFixed(1)}MB</p>
              </div>
            </div>
          </div>
        )}

        {/* Compact Element Repair Tool */}
        {showElementRepair && elements && elements.length > 0 && (
          <div className="mx-2 my-2 p-2 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg border border-orange-200/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-gradient-to-r from-orange-500 to-amber-600 rounded">
                  <Settings size={12} className="text-white" />
                </div>
                <h4 className="font-bold text-xs text-orange-800">Element Fix</h4>
              </div>
              <button
                onClick={handleElementRepair}
                disabled={isRepairing}
                className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                  isRepairing 
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:from-orange-700 hover:to-orange-600'
                }`}
              >
                {isRepairing ? (
                  <>
                    <div className="w-3 h-3 border-2 border-slate-500 border-t-transparent rounded-full animate-spin inline mr-1"></div>
                    Fixing...
                  </>
                ) : (
                  <>
                    <AlertTriangle size={10} className="inline mr-1" />
                    Repair
                  </>
                )}
              </button>
            </div>
            
            {/* Compact Element Counts */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              <div className="bg-white/60 rounded p-1.5 text-center">
                <p className="text-xs text-orange-600 font-medium">Total</p>
                <p className="text-sm font-bold text-orange-700">{elements.length}</p>
              </div>
              <div className="bg-white/60 rounded p-1.5 text-center">
                <p className="text-xs text-orange-600 font-medium">Lights</p>
                <p className="text-sm font-bold text-orange-700">
                  {elements.filter(el => ['light', 'smoke-alarm'].includes(el.type)).length}
                </p>
              </div>
              <div className="bg-white/60 rounded p-1.5 text-center">
                <p className="text-xs text-orange-600 font-medium">Radiators</p>
                <p className="text-sm font-bold text-orange-700">
                  {elements.filter(el => el.type === 'radiator').length}
                </p>
              </div>
              <div className="bg-white/60 rounded p-1.5 text-center">
                <p className="text-xs text-orange-600 font-medium">Windows</p>
                <p className="text-sm font-bold text-orange-700">
                  {elements.filter(el => el.type === 'window').length}
                </p>
              </div>
            </div>
            
            {/* Compact Repair Results */}
            {repairResult && (
              <div className={`p-2 rounded border ${
                repairResult.elementsRepaired 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                {repairResult.elementsRepaired ? (
                  <div className="text-emerald-800">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                      <span className="font-bold text-xs">Fixed {repairResult.reportSummary.elementsFixed} elements</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-blue-800">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="font-bold text-xs">All elements configured</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Compact Calculations Grid */}
        <div className="p-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* HLP Length - Heat Loss Perimeter */}
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-2 border border-red-200/50 hover:shadow-md transition-all duration-200 group">
              <div className="flex items-center justify-between mb-1">
                <div className="p-1 bg-gradient-to-r from-red-500 to-rose-600 rounded shadow group-hover:scale-105 transition-transform duration-200">
                  <Columns size={12} className="text-white" />
                </div>
                <span className="text-xs font-bold text-red-700 bg-red-200/80 px-1.5 py-0.5 rounded">HLP</span>
              </div>
              <div>
                <p className="text-xs text-red-600 font-medium hidden sm:block mb-0.5">Heat Loss Perimeter</p>
                <p className="text-sm sm:text-base font-bold text-red-700 leading-tight">
                  {calculations.hlpLength.toFixed(2)}
                  <span className="text-xs font-medium ml-0.5 text-red-600">m</span>
                </p>
              </div>
            </div>

            {/* PW Length - Party Wall */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2 border border-blue-200/50 hover:shadow-md transition-all duration-200 group">
              <div className="flex items-center justify-between mb-1">
                <div className="p-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded shadow group-hover:scale-105 transition-transform duration-200">
                  <Box size={12} className="text-white" />
                </div>
                <span className="text-xs font-bold text-blue-700 bg-blue-200/80 px-1.5 py-0.5 rounded">P/W</span>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium hidden sm:block mb-0.5">Party Wall Length</p>
                <p className="text-sm sm:text-base font-bold text-blue-700 leading-tight">
                  {calculations.pwLength.toFixed(2)}
                  <span className="text-xs font-medium ml-0.5 text-blue-600">m</span>
                </p>
              </div>
            </div>

            {/* SW Length - Separating Wall */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-2 border border-orange-200/50 hover:shadow-md transition-all duration-200 group">
              <div className="flex items-center justify-between mb-1">
                <div className="p-1 bg-gradient-to-r from-orange-500 to-amber-600 rounded shadow group-hover:scale-105 transition-transform duration-200">
                  <Target size={12} className="text-white" />
                </div>
                <span className="text-xs font-bold text-orange-700 bg-orange-200/80 px-1.5 py-0.5 rounded">SW</span>
              </div>
              <div>
                <p className="text-xs text-orange-600 font-medium hidden sm:block mb-0.5">Separating Wall</p>
                <p className="text-sm sm:text-base font-bold text-orange-700 leading-tight">
                  {calculations.swLength.toFixed(2)}
                  <span className="text-xs font-medium ml-0.5 text-orange-600">m</span>
                </p>
              </div>
            </div>

            {/* Total Area */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-2 border border-emerald-200/50 hover:shadow-md transition-all duration-200 group">
              <div className="flex items-center justify-between mb-1">
                <div className="p-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded shadow group-hover:scale-105 transition-transform duration-200">
                  <Square size={12} className="text-white" />
                </div>
                <span className="text-xs font-bold text-emerald-700 bg-emerald-200/80 px-1.5 py-0.5 rounded">AREA</span>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium hidden sm:block mb-0.5">Total Floor Area</p>
                <p className="text-sm sm:text-base font-bold text-emerald-700 leading-tight">
                  {calculations.totalArea.toFixed(2)}
                  <span className="text-xs font-medium ml-0.5 text-emerald-600">m²</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Status Footer */}
        <div className="mx-2 mb-2 p-2 bg-gradient-to-r from-slate-50 to-white rounded-lg border border-slate-200/50">
          <div className="flex justify-between items-center text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-slate-600 font-medium">
                <span className="hidden sm:inline">Updated: </span>
                {new Date(calculations.lastCalculated).toLocaleTimeString('en-US', { timeStyle: 'short' })}
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-0.5">
                <span className="text-slate-700 font-semibold">{currentWalls.length}</span>
                <span className="text-slate-500">W</span>
              </div>
              <div className="flex items-center gap-0.5">
                <span className="text-slate-700 font-semibold">{calculations.totalRooms}</span>
                <span className="text-slate-500">R</span>
              </div>
              <div className="hidden sm:flex items-center gap-0.5">
                <span className="text-slate-700 font-semibold">{performanceMetrics?.averageCalculationTime.toFixed(1)}</span>
                <span className="text-slate-500">ms</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnDemandCalculationsPanel; 